import Styles from './NodeParserEditor.module.scss';
import React from 'react';
import Highlighter from 'react-highlight-words';
import { TextField, Typography } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useTranslation } from 'components/lib';

/**
 * 
 * @param {string} parser can have values 'regex', 'json' or 'xml'. Defines which type of parser needs to executed
 * @param {string} matchedText the text that needs to be highlighted
 * @param {string} textToDisplay the text that needs to be displayed in the textbox
 * @param {string} currentHeading heading text in the parser
 * @param {string} userInput the userinput that is entered in the textfield
 * @returns 
 */
export function NodeParserEditor({
    parser,
    onInputChange,
    moveNext,
    movePrev,
    matchedText,
    textToDisplay,
    isCorrect,
    isDisabled,
    currentHeading,
    handleClick,
    userInput
}) {

    const { t } = useTranslation();

    const handleChange = (event) => {
        const newInput = event.target.value
        onInputChange(newInput)
    }

    const handleNextClick = () => {
        moveNext()
    }

    const handlePrevClick = () => {
        movePrev()
    }

    return (

        <div id="parentContainer" className={Styles.parentContainer}>
            <div className={Styles.headingStyles}>{currentHeading}</div>
            <div id="inputContainer" className={Styles.inputContainer}>
                <div id="regexTextContainer" className={Styles.regexTextContainer}>

                    <TextField
                        id='expressionTextField'
                        variant="outlined"
                        fullWidth
                        size="small"
                        autoFocus={true}
                        value={userInput}
                        onKeyDown={(ev) => {
                            if (ev.key === 'Enter') {
                                handleClick()
                                ev.preventDefault();
                            }
                        }}
                        placeholder={parser === 'xml' ?
                            t('Labs.Nodes.NodeParser.editor.placeholder.xpath')
                            : parser === 'json' ? t('Labs.Nodes.NodeParser.editor.placeholder.json') :
                                t('Labs.Nodes.NodeParser.editor.placeholder.regex')
                        }
                        onChange={handleChange}
                        sx={{
                            height: '40px',
                            borderRadius: '8px',
                            width: '100%',
                            bgcolor: 'ghostwhite',
                        }}
                    />
                </div>
                <div id="matchBtnContainer" className={Styles.matchBtnContainer}>
                    <button
                        id="matchBtn"
                        onClick={handleClick}
                        className={Styles.matchBtn}
                    >
                        {t('Labs.Nodes.NodeParser.editor.button')}
                    </button>
                </div>
            </div>
            <div id="textBoxContainer" className={Styles.textBoxContainer}>
                <div id='textBox' className={Styles.textBox}>
                    {parser === 'json' || parser === 'xml' ?
                        <>
                            <div id="jsonTextBox" className={Styles.jsonTextBox}>
                                <div id="jsonBodyTextBox"
                                    className={Styles.jsonBodyTextBox}
                                    style={{
                                        marginRight: '4px',
                                        fontFamily: parser === 'json' || parser === 'xml' ? 'monospace' : '',
                                        whiteSpace: parser === 'json' || parser === 'xml' ? 'pre' : '',
                                    }}
                                >
                                    {textToDisplay}
                                </div>
                                <div id="extractedTextBox"
                                    className={Styles.jsonBodyTextBox}
                                    style={{
                                        marginLeft: '4px',
                                        color: isCorrect ? '#00c853' : '#d50000',
                                        fontFamily: parser === 'json' || parser === 'xml' ? 'monospace' : '',
                                        whiteSpace: parser === 'json' || parser === 'xml' ? 'pre' : '',
                                    }}
                                >
                                    {matchedText}
                                </div>
                            </div>
                        </>
                        :
                        <Highlighter
                            searchWords={[matchedText]}
                            textToHighlight={textToDisplay}
                            autoEscape={parser === 'regex' ? false : true}
                            unhighlightStyle={{
                                fontFamily: parser === 'json' || parser === 'xml' ? 'monospace' : '',
                                whiteSpace: parser === 'json' || parser === 'xml' ? 'pre' : '',
                            }}
                            highlightStyle={{
                                backgroundColor: isCorrect ? '#5ff59b' : '#f97171',
                                fontFamily: parser === 'json' || parser === 'xml' ? 'monospace' : '',
                                whiteSpace: parser === 'json' || parser === 'xml' ? 'pre' : '',
                            }}
                        />
                    }

                </div>
            </div>
            <div className={Styles.navBtnContainer}>

                <div id='startButtonContainer' className={Styles.nextArrow}>
                    <button id='nextbtn' className={Styles.navButton}
                        onClick={handleNextClick}
                        disabled={isDisabled}
                    >
                        <Typography variant="button"
                            id="next"
                            sx={{
                                display: 'flex',
                                alignItems: "center",
                                justifyItems: "center",
                                flexDirection: "row",
                            }}>
                            <div id='nextButtonContainer'
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    justifyItems: 'center',
                                    alignItems: 'center',
                                    alignContent: 'center',
                                    gap: '2px'
                                }}
                            >
                                {t('Labs.Nodes.NodeParser.navButton.next')}
                                <LockOpenIcon
                                    fontSize='small'
                                    sx={{
                                        display: !isDisabled ? '' : 'none',
                                        animation: !isDisabled ? 'jumpUp 0.5s' : ''
                                    }}
                                />
                                <LockIcon
                                    fontSize='small'
                                    sx={{
                                        display: isDisabled ? '' : 'none',
                                        animation: isDisabled ? 'shake 0.3s' : ''
                                    }}
                                />
                            </div>
                        </Typography>
                    </button>
                </div>

                <div id='startButtonContainer' className={Styles.prevArrow}>
                    <button id='prevbtn' className={Styles.navButton}
                        style={{
                            color: "black"
                        }}
                        onClick={handlePrevClick}
                    >
                        <Typography variant="button"
                            id="prevButton"
                            sx={{
                                // color: "#000000",
                                display: 'flex',
                                alignItems: "center",
                                justifyItems: "center",
                                flexDirection: "row",
                            }}>
                            <KeyboardDoubleArrowLeftIcon />
                            {t('Labs.Nodes.NodeParser.navButton.prev')}
                        </Typography>
                    </button>
                </div>

            </div>
        </div>
    );
}
